import type { Aircraft } from "../aircraft/Aircraft";
import type {
  MutableVec2,
  RunwayDefinition,
  Vec2,
} from "../airport/AirportDefinition";
import { clonePoint, distance } from "./geometry";

export type LandingIntentStatus =
  "eligible" | "occupied" | "direction" | "color";

export interface LandingIntent {
  readonly status: LandingIntentStatus;
  readonly runway: RunwayDefinition;
}

/**
 * Thin out and smooth a freehand path.
 *
 * Points closer together than `spacing` are dropped, then every interior point
 * is pulled halfway towards its neighbours so the ribbon reads as a curve
 * instead of a polyline.
 */
export function simplifyPath(
  points: readonly Vec2[],
  spacing: number,
): MutableVec2[] {
  if (points.length <= 2) {
    return points.map(clonePoint);
  }

  const kept: Vec2[] = [points[0]!];
  let previous = points[0]!;
  for (let i = 1; i < points.length - 1; i++) {
    const point = points[i]!;
    if (distance(previous, point) >= spacing) {
      kept.push(point);
      previous = point;
    }
  }
  kept.push(points[points.length - 1]!);

  return kept.map((point, index) => {
    if (index === 0 || index === kept.length - 1) {
      return clonePoint(point);
    }
    const before = kept[index - 1]!;
    const after = kept[index + 1]!;
    return {
      x: point.x * 0.5 + (before.x + after.x) * 0.25,
      y: point.y * 0.5 + (before.y + after.y) * 0.25,
    };
  });
}

/** Nearest runway whose colour this aircraft is allowed to use. */
function nearestAllowedRunway(
  aircraft: Aircraft,
  point: Vec2,
  runways: readonly RunwayDefinition[],
  snapDistance: number,
): RunwayDefinition | null {
  let best: RunwayDefinition | null = null;
  let bestDistance = snapDistance;
  for (const runway of runways) {
    if (!aircraft.spec.allows.includes(runway.color)) {
      continue;
    }
    const gap = distance(point, runway.approach);
    if (gap <= bestDistance) {
      best = runway;
      bestDistance = gap;
    }
  }
  return best;
}

/**
 * Classify where the player finished a drag.
 *
 * - `eligible`  — a colour-matched approach gate: the landing is accepted.
 * - `occupied`  — the right gate, but the runway is already spoken for.
 * - `direction` — the wrong end of a runway: right runway, wrong way round.
 * - `color`     — a mismatched runway: wrong colour.
 *
 * `isClaimed` answers the occupancy question; it defaults to "never busy" so a
 * caller that only wants the geometry — a test, or the map validator — does
 * not have to invent an aircraft list. The check sits here rather than at
 * touchdown because the player has to hear "not that one, not yet" while the
 * pen is still down, when the route can still be redrawn.
 */
export function landingIntentFor(
  aircraft: Aircraft,
  point: Vec2,
  runways: readonly RunwayDefinition[],
  snapDistance: number,
  isClaimed: (runway: RunwayDefinition) => boolean = () => false,
): LandingIntent | null {
  const allowed = nearestAllowedRunway(aircraft, point, runways, snapDistance);
  if (allowed) {
    // Deliberately not falling through to the refusal searches below: the
    // player picked the right runway, and naming some other one they did not
    // aim at would answer a question they never asked.
    return {
      status: isClaimed(allowed) ? "occupied" : "eligible",
      runway: allowed,
    };
  }

  let wrongWay: RunwayDefinition | null = null;
  let wrongWayDistance = snapDistance;
  for (const runway of runways) {
    const gap = distance(point, runway.end);
    if (gap <= wrongWayDistance) {
      wrongWay = runway;
      wrongWayDistance = gap;
    }
  }
  if (wrongWay) {
    return { status: "direction", runway: wrongWay };
  }

  let wrongColor: RunwayDefinition | null = null;
  let wrongColorDistance = snapDistance;
  for (const runway of runways) {
    if (aircraft.spec.allows.includes(runway.color)) {
      continue;
    }
    const gap = distance(point, runway.approach);
    if (gap <= wrongColorDistance) {
      wrongColor = runway;
      wrongColorDistance = gap;
    }
  }
  return wrongColor ? { status: "color", runway: wrongColor } : null;
}

/**
 * Rebuild an accepted path so it ends flying down the runway centreline.
 *
 * The tail of the drawn path near the runway is discarded and replaced with a
 * cubic that lines the aircraft up behind the approach gate, unless it is
 * already lined up, in which case it goes straight in.
 */
export function buildApproachPath(
  from: Vec2,
  drawn: readonly Vec2[],
  approach: Vec2,
  end: Vec2,
  snapDistance: number,
  simplifySpacing: number,
): MutableVec2[] {
  const trimmed = drawn.map(clonePoint);
  while (
    trimmed.length > 0 &&
    distance(trimmed[trimmed.length - 1]!, approach) <= snapDistance
  ) {
    trimmed.pop();
  }

  const simplified = simplifyPath(trimmed, simplifySpacing);
  const tail = simplified[simplified.length - 1] ?? clonePoint(from);

  const axisX = end.x - approach.x;
  const axisY = end.y - approach.y;
  const axisLength = Math.hypot(axisX, axisY) || 1;
  const ux = axisX / axisLength;
  const uy = axisY / axisLength;

  // How far behind the approach gate the tail sits, along the runway axis.
  const behind = -((tail.x - approach.x) * ux + (tail.y - approach.y) * uy);
  if (behind <= 0.5) {
    return [...simplified, clonePoint(approach), clonePoint(end)];
  }

  const gap = distance(tail, approach);
  const towardsX = (approach.x - tail.x) / (gap || 1);
  const towardsY = (approach.y - tail.y) / (gap || 1);
  const outHandle = gap * 0.35;
  const inHandle = Math.min(gap * 0.3, behind * 0.5);
  const controlA = {
    x: tail.x + towardsX * outHandle,
    y: tail.y + towardsY * outHandle,
  };
  const controlB = {
    x: approach.x - ux * inHandle,
    y: approach.y - uy * inHandle,
  };

  const curve: MutableVec2[] = [];
  for (let step = 1; step <= 6; step++) {
    const t = step / 6;
    const r = 1 - t;
    curve.push({
      x:
        r * r * r * tail.x +
        3 * r * r * t * controlA.x +
        3 * r * t * t * controlB.x +
        t * t * t * approach.x,
      y:
        r * r * r * tail.y +
        3 * r * r * t * controlA.y +
        3 * r * t * t * controlB.y +
        t * t * t * approach.y,
    });
  }

  return [...simplified, ...curve, clonePoint(end)];
}
